import type { ChatState } from './interfaces.chat';
import { chatHistoryReducer } from './state.chat-history';
import { chatSessionReducer } from './state.chat-session';
import { chatSessionManagerReducer } from './state.chat-session-manager';
import { generateEmptyChatState } from './state.initial-state';
import { shouldShowLandingCardsAfterReset } from './state.landing-cards';
import { settingsReducer } from './state.settings';

function baseChatReducer(state: ChatState.State, action: ChatState.Action): ChatState.State {
  const newState = computeNewState(state, action);

  logAction(state, newState, action);

  return newState;
}

function batchActions(state: ChatState.State, { payload }: ChatState.BatchAction): ChatState.State {
  let updatedState = state;
  for (const action of payload.actions) {
    updatedState = baseChatReducer(updatedState, action);
  }

  return updatedState;
}

export function chatReducer(state: ChatState.State, action: ChatState.Action): ChatState.State {
  switch (action.type) {
    case 'batch-actions':
      return batchActions(state, action);

    default:
      return baseChatReducer(state, action);
  }
}

function computeNewState(state: ChatState.State, action: ChatState.Action): ChatState.State {
  switch (action.type) {
    case 'reset-chat-state':
      return generateEmptyChatState({
        contextItems: state.contextItems,
        context: action.payload.context,
        settings: state.settings,
        features: state.settings,
        brandIdentifier: action.payload.brandIdentifier,
        landingCardsVisible: shouldShowLandingCardsAfterReset(action.payload.context),
      });

    default: {
      const newState: ChatState.State = {
        contextItems: state.contextItems,
        settings: settingsReducer(state.settings, action),
        history: chatHistoryReducer(state.history, action),
        session: chatSessionReducer(state, action),
        sessionManager: chatSessionManagerReducer(state.sessionManager, action),
        unsavedTabSnapshots: state.unsavedTabSnapshots,
      };

      newState.history = {
        ...newState.history,
        tabs: syncSessionTab(state, newState),
      };

      newState.unsavedTabSnapshots = syncUnsavedTabSnapshots(state, newState);

      return newState;
    }
  }
}

function syncSessionTab(
  prevState: ChatState.State,
  newState: ChatState.State
): ChatState.State['history']['tabs'] {
  const tabs = newState.history.tabs;
  const sessionIdentifier = newState.session.chatInstance.identifier;
  const sessionTabIndex = tabs.items.findIndex((identifier) => identifier === sessionIdentifier);
  const sessionChanged = prevState.session.chatInstance.identifier !== sessionIdentifier;
  const landingCardsJustClosed =
    prevState.session.landingCardsVisible && !newState.session.landingCardsVisible;

  if (newState.session.landingCardsVisible) {
    if (sessionTabIndex === -1) {
      if (tabs.index === -1) {
        return tabs;
      }

      return {
        ...tabs,
        index: -1,
      };
    }

    const items = tabs.items.filter((identifier) => identifier !== sessionIdentifier);

    return {
      ...tabs,
      items,
      index: -1,
    };
  }

  if (sessionTabIndex !== -1) {
    return tabs;
  }

  // Only auto-add when a new session is created, or when landing cards transition
  // from visible to hidden for the current session.
  if (!sessionChanged && !landingCardsJustClosed) {
    return tabs;
  }

  return {
    items: tabs.items.concat(sessionIdentifier),
    index: tabs.items.length,
  };
}

function syncUnsavedTabSnapshots(
  prevState: ChatState.State,
  newState: ChatState.State
): ChatState.State['unsavedTabSnapshots'] {
  const nextSnapshots: ChatState.State['unsavedTabSnapshots'] = {
    ...prevState.unsavedTabSnapshots,
  };
  const activeSession = newState.session;
  const activeIdentifier = activeSession.chatInstance.identifier;

  if (
    !activeSession.landingCardsVisible &&
    activeSession.chatInstance.id == null &&
    newState.history.tabs.items.includes(activeIdentifier)
  ) {
    nextSnapshots[activeIdentifier] = {
      chatInstance: activeSession.chatInstance,
      filters: activeSession.filters,
      landingCardsVisible: activeSession.landingCardsVisible,
    };
  }

  const tabIdentifiers = new Set(newState.history.tabs.items);

  for (const identifier of Object.keys(nextSnapshots)) {
    if (!tabIdentifiers.has(identifier)) {
      delete nextSnapshots[identifier];
      continue;
    }

    if (isPersistedTabIdentifier(newState, identifier)) {
      delete nextSnapshots[identifier];
    }
  }

  return nextSnapshots;
}

function isPersistedTabIdentifier(state: ChatState.State, identifier: string): boolean {
  if (state.session.chatInstance.identifier === identifier) {
    return state.session.chatInstance.id != null;
  }

  const historyMatch =
    state.history.items.find((item) => item.identifier === identifier) ||
    state.history.shared.find((item) => item.identifier === identifier);

  if (historyMatch) {
    return historyMatch.id != null;
  }

  const snapshot = state.unsavedTabSnapshots[identifier];
  return snapshot ? snapshot.chatInstance.id != null : false;
}

function logAction(_oldState: ChatState.State, _newState: ChatState.State, _action: ChatState.Action) {}
